import { useMsal } from "@azure/msal-react";
import axios, { AxiosResponse, CancelToken, CancelTokenSource } from "axios";
import { useEffect, useRef } from "react";
import { useHistoryPeriod } from "../Components/Contexts/HistoryPeriodProvider";
import { useLoggedInUser } from "../Components/Contexts/LoggedInUserProvider";
import {
  getBaseHeaders,
  getBudgetColumns,
  getBudgetCopyColumns,
  getHeadColumns,
  getHistoryBudgetColumns,
  getHistoryFteColumns,
  getOriginalFteColumns,
  getOwnerColumns,
} from "./DataHelpers";

const useDataverseService = () => {
  const { instance, accounts } = useMsal();
  const { loggedInUserId } = useLoggedInUser();
  const { historyPeriod } = useHistoryPeriod();
  const cancelTokenSource = useRef<CancelTokenSource | null>(null);

  const getAccessToken = async (): Promise<string> => {
    const request = {
      scopes: [`${process.env["REACT_APP_DATAVERSE_URL"]}.default`],
      account: accounts[0],
    };
    const response = await instance.acquireTokenSilent(request);
    return response.accessToken;
  };

  const getUsers = async (): Promise<any[]> => {
    const token = await getAccessToken();
    const response = await axios.get(
      `${process.env["REACT_APP_DATAVERSE_URL"]}api/data/v9.0/systemusers`,
      {
        headers: getBaseHeaders(token),
      }
    );
    return response.data.value;
  };

  const getCostCenters = async (): Promise<any[]> => {
    const token = await getAccessToken();
    const response = await axios.get(
      `${process.env["REACT_APP_DATAVERSE_URL"]}api/data/v9.1/new_costcenterses`,
      {
        headers: getBaseHeaders(token),
      }
    );
    return response.data.value;
  };

  const associateRecord = async (
    entitySetName: string,
    recordId: string,
    relatedEntitySetName: string,
    relatedRecordId: string,
    cancelToken?: CancelToken
  ): Promise<AxiosResponse | undefined> => {
    try {
      const token = await getAccessToken();
      const payload = {
        ["ownerid@odata.bind"]: `/${relatedEntitySetName}(${relatedRecordId})`,
      };

      const response = await axios.patch(
        `${process.env["REACT_APP_DATAVERSE_URL"]}api/data/v9.2/${entitySetName}(${recordId})`,
        payload,
        {
          headers: {
            ...getBaseHeaders(token),
            "If-Match": "*",
          },
          cancelToken,
        }
      );
      return response;
    } catch (error: any) {
      if (axios.isCancel(error)) {
        console.error(error);
      } else {
        throw error;
      }
    }
  };

  const putDataBatch = async (requests: any[]): Promise<any> => {
    try {
      const token = await getAccessToken();
      const batchBoundary = `batch_${new Date().getTime()}`;
      const changeSetBoundary = `changeset_${new Date().getTime()}`;

      const formatRequestBody = (body: Record<string, any>) => {
        const formattedBody: Record<string, any> = {};
        for (const key in body) {
          if (body.hasOwnProperty(key)) {
            const value = body[key];
            if (
              typeof value === "string" &&
              !isNaN(Number(value)) &&
              value.trim() !== ""
            ) {
              formattedBody[key] = parseFloat(value);
            } else {
              formattedBody[key] = value;
            }
          }
        }
        return formattedBody;
      };

      let batchBody = `--${batchBoundary}\nContent-Type: multipart/mixed; boundary=${changeSetBoundary}\n\n`;

      requests.forEach((request) => {
        batchBody += `--${changeSetBoundary}\nContent-Type: application/http\nContent-Transfer-Encoding: binary\nContent-ID: ${request.contentId}\n\n`;
        batchBody += `${request.method} ${request.url} HTTP/1.1\nContent-Type: application/json;type=entry\n\n`;
        batchBody += `${JSON.stringify(formatRequestBody(request.body))}\n\n`;
      });

      batchBody += `--${changeSetBoundary}--\n--${batchBoundary}--`;

      const response = await axios.post(
        `${process.env["REACT_APP_DATAVERSE_URL"]}api/data/v9.1/$batch`,
        batchBody,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": `multipart/mixed; boundary=${batchBoundary}`,
            "OData-MaxVersion": "4.0",
            "OData-Version": "4.0",
          },
        }
      );

      return response.data;
    } catch (error) {
      throw error;
    }
  };

  const patchEntity = async (
    entitySetName: string,
    entityId: string,
    patchData: Record<string, any>
  ): Promise<void> => {
    const token = await getAccessToken();
    await axios.patch(
      `${process.env["REACT_APP_DATAVERSE_URL"]}api/data/v9.1/${entitySetName}(${entityId})`,
      patchData,
      {
        headers: getBaseHeaders(token),
      }
    );
  };

  const fetchPage = async (
    url: string,
    cancelToken?: CancelToken
  ): Promise<{ data: any; nextLink?: string } | undefined> => {
    try {
      const token = await getAccessToken();
      const response = await axios.get(url, {
        headers: {
          ...getBaseHeaders(token),
          "Content-Type": "application/json odata.metadata=minimal",
          Prefer: "odata.maxpagesize=750",
        },
        cancelToken,
      });
      return {
        data: response.data,
        nextLink: response.data["@odata.nextLink"],
      };
    } catch (error: any) {
      if (axios.isCancel(error)) {
        console.error(error);
      } else {
        throw error;
      }
    }
  };

  const fetchData = async (
    entitySetName: string,
    cancelToken?: CancelToken,
    selectedCompanyCode: string | null = null
  ): Promise<any> => {
    let columns: string[] = [];
    let filter: string = "";
    const periodFilter = historyPeriod
      ? ` and new_period eq '${historyPeriod}'`
      : "";
    const assignedFilter = loggedInUserId
      ? `_ownerid_value eq '${loggedInUserId}' and `
      : "";
    switch (entitySetName) {
      case "cr864_budgetrecords":
        columns = getBudgetColumns();
        filter = `&$filter=${assignedFilter}cr864_codecompany eq '${selectedCompanyCode}'`;
        break;
      case "cr864_budgetrecordcopies":
        columns = getBudgetCopyColumns();
        filter = `&$filter=${assignedFilter}cr864_codecompany eq '${selectedCompanyCode}'`;
        break;
      case "new_entityowners":
        columns = getOwnerColumns();
        break;
      case "new_originalftes":
        columns = getOriginalFteColumns();
        filter = `&$filter=${assignedFilter}new_codecompany eq '${selectedCompanyCode}'`;
        break;
      case "new_historybudgets":
        columns = getHistoryBudgetColumns();
        filter = `&$filter=${assignedFilter}new_codecompany eq '${selectedCompanyCode}'${periodFilter}`;
        break;
      case "new_historyftes":
        columns = getHistoryFteColumns();
        filter = `&$filter=${assignedFilter}new_codecompany eq '${selectedCompanyCode}'${periodFilter}`;
        break;
      default:
        columns = getHeadColumns();
        filter = `&$filter=${assignedFilter}new_codecompany eq '${selectedCompanyCode}'`;
        break;
    }

    const initialUrl = `${
      process.env["REACT_APP_DATAVERSE_URL"]
    }api/data/v9.1/${entitySetName}?$select=${columns.join(",")}${filter}`;
    return fetchPage(initialUrl, cancelToken);
  };

  const fetchNextPage = async (nextLink: string, cancelToken?: CancelToken) => {
    return fetchPage(nextLink, cancelToken);
  };

  const fetchDistinctValues = async (
    entitySetName: string,
    columnName: string,
    cancelToken?: CancelToken,
    selectedCompanyCode?: string,
    selectedTab?: string
  ): Promise<any> => {
    let distinctFilter = "";
    const assignedFilter = loggedInUserId
      ? ` and _ownerid_value eq '${loggedInUserId}'`
      : "";
    if (selectedCompanyCode) {
      distinctFilter =
        selectedTab === "Summary Unified Category"
          ? `&$filter=cr864_codecompany eq '${selectedCompanyCode}'${assignedFilter}`
          : `&$filter=new_codecompany eq '${selectedCompanyCode}'${assignedFilter}`;
    }
    else{
      const users = await getUsers();
      const currentUser = users.find(
        (u: any) => u.domainname === instance.getActiveAccount()?.username
      );
      distinctFilter = `&$filter=_ownerid_value eq '${currentUser.systemuserid}'`
    }
    try {
      const token = await getAccessToken();
      const response = await axios.get(
        `${process.env["REACT_APP_DATAVERSE_URL"]}api/data/v9.1/${entitySetName}?$apply=groupby((${columnName}))${distinctFilter}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          cancelToken,
        }
      );
      return response.data;
    } catch (error: any) {
      if (axios.isCancel(error)) {
        console.error(error);
      } else {
        throw error;
      }
    }
  };

  useEffect(() => {
    return () => {
      if (cancelTokenSource.current) {
        cancelTokenSource.current.cancel("Operation canceled by the user.");
      }
    };
  }, []);

  return {
    fetchData,
    fetchNextPage,
    putDataBatch,
    getUsers,
    getCostCenters,
    associateRecord,
    fetchDistinctValues,
    patchEntity,
  };
};

export default useDataverseService;
